/** @jsxImportSource @emotion/react */
import { css, type Interpolation } from "@emotion/react";
import { type Theme } from "../../styles";
import { theme } from "../../styles/theme/theme";
import { Slot } from "@radix-ui/react-slot";
import * as React from "react";

const baseButtonStyles = css`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  white-space: nowrap;
  border-radius: 0.375rem;
  font-size: 0.875rem;
  font-weight: 500;
  transition: all 0.2s;
  outline: none;
  flex-shrink: 0;

  &:disabled {
    pointer-events: none;
    opacity: 0.5;
  }

  & svg {
    pointer-events: none;
    width: 1rem;
    height: 1rem;
    flex-shrink: 0;
  }

  &:focus-visible {
    border-color: ${theme.colors.ring};
    box-shadow: 0 0 0 3px color-mix(in srgb, ${theme.colors.ring} 50%, transparent);
  }

  &[aria-invalid="true"] {
    border-color: ${theme.colors.destructive};
    box-shadow: 0 0 0 3px color-mix(in srgb, ${theme.colors.destructive} 20%, transparent);
  }
`;

const variantStyles: Record<string, Interpolation<Theme>> = {
  default: css`
    background-color: ${theme.colors.primary};
    color: ${theme.colors.primaryForeground};
    box-shadow: ${theme.shadows.xs};

    &:hover {
      background-color: color-mix(in srgb, ${theme.colors.primary} 90%, transparent);
    }
  `,
  destructive: css`
    background-color: ${theme.colors.destructive};
    color: white;
    box-shadow: ${theme.shadows.xs};

    &:hover {
      background-color: color-mix(in srgb, ${theme.colors.destructive} 90%, transparent);
    }

    &:focus-visible {
      box-shadow: 0 0 0 3px color-mix(in srgb, ${theme.colors.destructive} 20%, transparent);
    }
  `,
  outline: css`
    border: 1px solid ${theme.colors.input};
    background-color: ${theme.colors.background};
    box-shadow: ${theme.shadows.xs};

    &:hover {
      background-color: ${theme.colors.accent};
      color: ${theme.colors.accentForeground};
    }
  `,
  secondary: css`
    background-color: ${theme.colors.secondary};
    color: ${theme.colors.secondaryForeground};
    box-shadow: ${theme.shadows.xs};

    &:hover {
      background-color: color-mix(in srgb, ${theme.colors.secondary} 80%, transparent);
    }
  `,
  secondaryDark: css`
    background-color: ${theme.colors.secondaryDark};
    color: ${theme.colors.white};
    box-shadow: ${theme.shadows.xs};

    &:hover {
      background-color: ${theme.colors.secondaryDarkHover};
    }
  `,
  ghost: css`
    &:hover {
      background-color: ${theme.colors.accent};
      color: ${theme.colors.accentForeground};
    }
  `,
  link: css`
    color: ${theme.colors.primary};
    text-decoration: underline;
    text-underline-offset: 4px;

    &:hover {
      text-decoration: underline;
    }
  `,
};

const sizeStyles: Record<string, Interpolation<Theme>> = {
  default: css`
    height: 2.25rem;
    padding: 0.5rem 1rem;

    &:has(> svg) {
      padding-left: 0.75rem;
    }
  `,
  sm: css`
    height: 2rem;
    border-radius: 0.375rem;
    gap: 0.375rem;
    padding: 0 0.75rem;

    &:has(> svg) {
      padding-left: 0.625rem;
    }
  `,
  lg: css`
    height: 2.5rem;
    border-radius: 0.375rem;
    padding: 0 1.5rem;

    &:has(> svg) {
      padding-left: 1rem;
    }
  `,
  icon: css`
    width: 2.25rem;
    height: 2.25rem;
  `,
};

type ButtonVariant = "default" | "destructive" | "outline" | "secondary" | "secondaryDark" | "ghost" | "link";
type ButtonSize = "default" | "sm" | "lg" | "icon";

function Button({
  className,
  variant = "default",
  size = "default",
  asChild = false,
  css: cssProp,
  ...props
}: React.ComponentProps<"button"> & {
  variant?: ButtonVariant;
  size?: ButtonSize;
  asChild?: boolean;
  css?: Interpolation<Theme>;
}) {
  const Comp = asChild ? Slot : "button";

  return (
    <Comp
      data-slot="button"
      css={[baseButtonStyles, variantStyles[variant], sizeStyles[size], cssProp]}
      className={className}
      {...props}
    />
  );
}

export { Button };
